import type { Span } from '@sentry/core';
import { debug, getActiveSpan, SPAN_STATUS_ERROR, withActiveSpan } from '@sentry/core';
import { DEBUG_BUILD } from '../debug-build';
import { CHANNELS } from '../orchestrion/channels';
import { bindTracingChannelToSpan, type TracingChannelPayloadWithSpan } from '../tracing-channel';
import {
  clearOperationCallId,
  clearOperationId,
  createSpanFromMessage,
  enrichSpanOnEnd,
  type VercelAiChannelMessage,
  type VercelAiChannelOptions,
  type VercelAiTracingChannelFactory,
} from './vercel-ai-dc-subscriber';

/**
 * v6 channel adapter for the Vercel AI (`ai`) SDK.
 *
 * `ai` >= 7 publishes a normalized `ai:telemetry` tracing channel natively
 * (consumed by `subscribeVercelAiTracingChannel`). v6 has no such channel, so
 * orchestrion injects `orchestrion:ai:*` channels around the top-level
 * functions (see `orchestrion/config/index.ts`). The injected channels carry only the
 * wrapped call's `{ arguments, result, error }` — NOT v7's normalized `event`
 * object — so this adapter reconstructs an equivalent {@link VercelAiChannelMessage}
 * from v6's argument/result shapes and delegates to the SAME span-building core
 * (`createSpanFromMessage` / `enrichSpanOnEnd`) the v7 subscriber uses, so the
 * emitted spans are identical between v6 and v7.
 *
 * The model call (`languageModelCall` / `generate_content` span) has no
 * injectable definition in `ai`, so we instead wrap `resolveLanguageModel` (the
 * single chokepoint every model call flows through) and monkey-patch
 * `doGenerate`/`doStream` on the returned model.
 */

/** Shape orchestrion's transform attaches to the tracing-channel context. */
interface OrchestrionContext {
  arguments: unknown[];
  result?: unknown;
  error?: unknown;
}

/** Builds the normalized message for a channel from the wrapped call's first-arg options. */
type MessageBuilder = (options: Record<string, unknown>, telemetry: Record<string, unknown>) => VercelAiChannelMessage;

/** A resolved `ai` language model — has `doGenerate`/`doStream` and identity fields. */
interface ResolvedModel {
  modelId?: string;
  provider?: string;
  doGenerate?: (...args: unknown[]) => Promise<unknown>;
  doStream?: (...args: unknown[]) => Promise<unknown>;
}

const PATCHED = Symbol('SentryVercelAiModelPatched');

/** A resolved model with our patch bookkeeping (idempotency flag). */
type PatchableModel = ResolvedModel & { [PATCHED]?: boolean };

// Per-operation correlation id. No Date/random (unavailable / non-deterministic) — a counter is enough.
let callIdCounter = 0;
function nextCallId(): string {
  return `v6-${++callIdCounter}`;
}

// The message built on `start` for each operation, keyed by the (stable-identity) channel context, so
// the `beforeSpanEnd` handler can enrich the span from the settled result and clear the `callId` maps.
const messages = new WeakMap<object, VercelAiChannelMessage>();
// The spans we opened for top-level operations, and each one's `callId`. A model call resolves its
// parent against this set (so it never mis-attributes to the enclosing `main`/user span) and reads the
// parent's `callId` so its span can be named after the operation (e.g. `ai.streamText.doStream`).
const operationSpans = new WeakSet<Span>();
const callIdBySpan = new WeakMap<Span, string>();
// The operation's per-call recording flags (`experimental_telemetry.recordInputs/recordOutputs`), keyed by
// its span. A model call carries no telemetry of its own, so it inherits the enclosing operation's flags —
// otherwise a per-call `recordInputs: true` would record inputs on the `invoke_agent` span but not on the
// child `generate_content` span (whose event would fall back to the global default). v7's channel forwards
// these flags on every event, so this keeps v6 identical.
const recordingBySpan = new WeakMap<Span, ReturnType<typeof recording>>();

// The `experimental_telemetry` objects we swapped in to suppress `ai`'s native OTel spans (see
// `suppressNativeTelemetry`). Our skip logic treats `isEnabled === false` as "user disabled telemetry,
// emit no span"; without this set, a call whose options object we already neutralized — or a user-shared
// telemetry object we replaced on a prior call — would be misread as user-disabled and lose its span.
const suppressedTelemetry = new WeakSet<object>();

let subscribed = false;

/**
 * Subscribe the v6 orchestrion channel adapter. Safe to always call: inert on
 * `ai` >= 7 (those channels are never published) and when orchestrion injection
 * isn't active. Idempotent.
 *
 * `tracingChannel` is the platform-provided factory (the same one passed to
 * `subscribeVercelAiTracingChannel`); `options` pins the recording settings at
 * subscribe time so we never look the integration up per event.
 */
export function subscribeVercelAiOrchestrionChannels(
  tracingChannel: VercelAiTracingChannelFactory,
  options: VercelAiChannelOptions = {},
): void {
  if (subscribed) {
    return;
  }
  subscribed = true;

  try {
    bindOperation(tracingChannel, CHANNELS.VERCEL_AI_GENERATE_TEXT, buildTextMessage('generateText'), options);
    bindOperation(tracingChannel, CHANNELS.VERCEL_AI_STREAM_TEXT, buildTextMessage('streamText'), options);
    bindOperation(
      tracingChannel,
      CHANNELS.VERCEL_AI_EMBED,
      (callOptions, telemetry) => ({
        type: 'embed',
        event: {
          callId: nextCallId(),
          ...modelFields(callOptions.model),
          maxRetries: callOptions.maxRetries,
          value: callOptions.value,
          ...recording(telemetry),
        },
      }),
      options,
    );
    bindOperation(
      tracingChannel,
      CHANNELS.VERCEL_AI_EMBED_MANY,
      // `embedMany` takes a `values` array (vs `embed`'s single `value`); the shared core reads it as the
      // embeddings input, matching the OTel path's batch `ai.embedMany` span.
      (callOptions, telemetry) => ({
        type: 'embedMany',
        event: {
          callId: nextCallId(),
          ...modelFields(callOptions.model),
          maxRetries: callOptions.maxRetries,
          values: callOptions.values,
          ...recording(telemetry),
        },
      }),
      options,
    );
    bindOperation(
      tracingChannel,
      CHANNELS.VERCEL_AI_EXECUTE_TOOL_CALL,
      (callOptions, telemetry) => ({
        type: 'executeTool',
        // v6 carries the tool definitions on the executeToolCall args (a record keyed by name);
        // the shared core reads the matching tool's `description` for the span.
        event: {
          callId: nextCallId(),
          toolCall: callOptions.toolCall,
          tools: callOptions.tools,
          ...recording(telemetry),
        },
      }),
      options,
    );
    subscribeResolveLanguageModel(tracingChannel, CHANNELS.VERCEL_AI_RESOLVE_LANGUAGE_MODEL, options);
  } catch {
    DEBUG_BUILD && debug.log('Vercel AI orchestrion channel subscription failed.');
  }
}

/**
 * Bind one operation channel: `getSpan` opens a span from the message reconstructed out of the wrapped
 * call's first argument; `beforeSpanEnd` enriches it from the settled result (tokens, output messages,
 * finish reasons, …) before the helper ends the span.
 *
 * An operation whose `experimental_telemetry.isEnabled` is explicitly `false` is skipped entirely (no
 * span): the orchestrion channel fires regardless of that flag, whereas v7's native `ai:telemetry`
 * channel is simply not published in that case — so we reproduce v7's "no telemetry → no span".
 */
function bindOperation(
  tracingChannel: VercelAiTracingChannelFactory,
  channelName: string,
  build: MessageBuilder,
  options: VercelAiChannelOptions,
): void {
  const channel = tracingChannel<OrchestrionContext>(channelName);

  // Build the operation span from the wrapped call's first argument and track it (so a model call can
  // resolve it as its parent). `bindTracingChannelToSpan` calls this once at channel `start` and makes
  // the returned span the active async context for the operation's duration — that active span is what
  // `resolveModelCallParent` reads. It also sets `data._sentrySpan`, so we don't here.
  const buildOperationSpan = (data: TracingChannelPayloadWithSpan<OrchestrionContext>): Span | undefined => {
    const callOptions = isRecord(data.arguments[0]) ? data.arguments[0] : {};
    const telemetry = isRecord(callOptions.experimental_telemetry) ? callOptions.experimental_telemetry : {};
    // `isEnabled === false` means the user opted out — emit no span. But `isEnabled` is also `false` on
    // the telemetry object we swap in to suppress native spans, so don't mistake our own object for a
    // user opt-out (which would drop the span on a call whose options we already neutralized).
    if (telemetry.isEnabled === false && !suppressedTelemetry.has(telemetry)) {
      return undefined;
    }
    const message = build(callOptions, telemetry);
    // Stop `ai` from emitting its own native OTel spans for this call — we build the equivalent spans
    // from the channels, so the SDK's would be duplicates. Reads above have already captured everything
    // we need off `telemetry`.
    suppressNativeTelemetry(callOptions, telemetry);
    const span = createSpanFromMessage(message, options);
    if (span) {
      messages.set(data, message);
      operationSpans.add(span);
      const callId = asString(message.event.callId);
      if (callId) {
        callIdBySpan.set(span, callId);
      }
      recordingBySpan.set(span, recording(telemetry));
    }
    return span;
  };

  bindTracingChannelToSpan(
    channel,
    (data: TracingChannelPayloadWithSpan<OrchestrionContext>) => buildOperationSpan(data),
    {
      beforeSpanEnd: (span, data) => {
        const message = messages.get(data);
        if (!message) {
          return;
        }
        // The helper's `error` handler already set the span status; only enrich from a successful result.
        if (!('error' in data)) {
          // v6's `executeToolCall` returns the tool result/error object directly, whereas the shared core
          // (matching v7) expects it nested under `output`; wrap it so tool-error detection works.
          message.result = message.type === 'executeTool' ? { output: data.result } : data.result;
          enrichSpanOnEnd(span, message, options);
        }
        // A `streamText` model call runs after this (synchronously-returning) operation's span has
        // already ended, so its `callId` entry must outlive the operation — it's cleared once the model
        // call settles (see `patchModelMethod`). Every other operation can clear here.
        if (message.type !== 'streamText') {
          clearOperationId(message);
        }
        messages.delete(data);
      },
    },
  );
}

/**
 * Neutralize `ai`'s native OpenTelemetry instrumentation for this call by pointing
 * `experimental_telemetry` at a copy with `isEnabled: false`. `ai`'s `getTracer` then returns its
 * internal no-op tracer, so it never creates (nor sets active) the duplicate `ai.*` spans we'd
 * otherwise have to drop.
 *
 * Only a call that explicitly enabled telemetry emits native spans — otherwise `ai` already uses its
 * no-op tracer, so there's nothing to suppress and we leave the user's options untouched. When
 * `isEnabled === true`, `telemetry` is `callOptions.experimental_telemetry` and `callOptions` is the
 * real first argument the SDK will read, so the reassignment takes effect for the wrapped call. We
 * replace rather than mutate in place, so a telemetry object the user shares across calls keeps its own
 * `isEnabled: true`; the replacement is tracked in `suppressedTelemetry` so our skip logic doesn't
 * later read it back as a user opt-out.
 */
function suppressNativeTelemetry(callOptions: Record<string, unknown>, telemetry: Record<string, unknown>): void {
  if (telemetry.isEnabled !== true) {
    return;
  }
  const suppressed = { ...telemetry, isEnabled: false };
  suppressedTelemetry.add(suppressed);
  callOptions.experimental_telemetry = suppressed;
}

/**
 * `resolveLanguageModel` returns the model every call flows through. We don't span it — on `end` we
 * monkey-patch `doGenerate`/`doStream` on the returned model so each invocation produces a
 * `languageModelCall` span parented to the enclosing invoke_agent span.
 */
function subscribeResolveLanguageModel(
  tracingChannel: VercelAiTracingChannelFactory,
  channelName: string,
  options: VercelAiChannelOptions,
): void {
  tracingChannel<OrchestrionContext>(channelName).subscribe({
    end(rawCtx) {
      const ctx = rawCtx as OrchestrionContext;
      if (!isRecord(ctx.result)) {
        return;
      }
      const model = ctx.result as PatchableModel;
      // Patch the model's `doGenerate`/`doStream` once. The model call recovers its parent from the
      // active async context at call time (the operation span `bindTracingChannelToSpan` bound), which
      // propagates into the model call for `streamText` too, so there is nothing to capture on the model here.
      if (!model[PATCHED]) {
        model[PATCHED] = true;
        patchModelMethod(model, 'doGenerate', options);
        patchModelMethod(model, 'doStream', options);
      }
    },
    start() {
      /* no-op */
    },
    asyncStart() {
      /* no-op */
    },
    asyncEnd() {
      /* no-op */
    },
    error() {
      /* no-op */
    },
  });
}

/**
 * Pick the invoke_agent span a model call should hang under: the operation span that
 * `bindTracingChannelToSpan` planted as the active async context for the enclosing operation.
 *
 * Because we suppress `ai`'s native telemetry (so it never installs its own span as active), the active
 * span inside `doGenerate`/`doStream` is our operation span. The `operationSpans` gate makes this return
 * `undefined` when the active span isn't one of ours — e.g. telemetry was disabled for the call so we
 * opened no operation span and the active span is the user's enclosing span — so the model call is
 * skipped rather than mis-parented.
 *
 * This covers `generateText`/`embed` (whose model call is awaited inside the operation body) and
 * `streamText` alike — `ai` initiates the stream synchronously within the operation's bound context, so
 * the later `doStream` continuation restores the same active span even though the operation's span has
 * already ended. The per-operation binding also disambiguates concurrent calls that share one model
 * instance (a single mutable slot on the shared model could not — it would hold whichever operation
 * resolved the model last).
 */
function resolveModelCallParent(): Span | undefined {
  const active = getActiveSpan();
  return active && operationSpans.has(active) ? active : undefined;
}

function patchModelMethod(
  model: PatchableModel,
  method: 'doGenerate' | 'doStream',
  options: VercelAiChannelOptions,
): void {
  const original = model[method];
  if (typeof original !== 'function') {
    return;
  }
  model[method] = function (this: unknown, ...args: unknown[]): Promise<unknown> {
    const parent = resolveModelCallParent();
    // No enclosing operation span (e.g. telemetry disabled for the call) → don't open a model-call span.
    if (!parent) {
      return Promise.resolve(original.apply(this, args));
    }

    const callArgs = isRecord(args[0]) ? args[0] : {};
    // Carry the operation's `callId` so the shared core can name the span after it
    // (`ai.generateText.doGenerate` / `ai.streamText.doStream`).
    const callId = callIdBySpan.get(parent);
    const message: VercelAiChannelMessage = {
      type: 'languageModelCall',
      event: {
        callId,
        provider: model.provider,
        modelId: model.modelId,
        tools: callArgs.tools,
        messages: callArgs.prompt,
        // Inherit the enclosing operation's per-call recording flags so inputs/tools/outputs are recorded on
        // the model-call span whenever they are on the parent `invoke_agent` span.
        ...recordingBySpan.get(parent),
      },
    };
    const span = withActiveSpan(parent, () => createSpanFromMessage(message, options));
    // `languageModelCall` always opens a span; the guard just keeps the wrapper safe if that changes.
    if (!span) {
      return Promise.resolve(original.apply(this, args));
    }

    // `streamText` ends its operation span synchronously, so its `callId` entry was deliberately left in
    // place for this later model call; drop it now that we've used it.
    const clearStreamCallId = (): void => {
      if (method === 'doStream' && callId) {
        clearOperationCallId(callId);
      }
    };

    // Both the synchronous throw and the async rejection of the model call must end the span with an
    // error status (an `async` `doGenerate`/`doStream` that throws rejects rather than throwing here).
    const failSpan = (error: unknown): never => {
      span.setStatus({ code: SPAN_STATUS_ERROR, message: error instanceof Error ? error.message : 'unknown_error' });
      span.end();
      clearStreamCallId();
      throw error;
    };

    try {
      const result = Promise.resolve(original.apply(this, args));
      // `doStream` resolves to `{ stream, ... }` before the stream is consumed; we end here (start/end
      // bracket the call) to match the channel timing.
      return result.then(value => {
        message.result = value;
        enrichSpanOnEnd(span, message, options);
        span.end();
        clearStreamCallId();
        return value;
      }, failSpan);
    } catch (error) {
      return failSpan(error);
    }
  };
}

function buildTextMessage(type: 'generateText' | 'streamText'): MessageBuilder {
  return (options, telemetry) => ({
    type,
    event: {
      callId: nextCallId(),
      operationId: `ai.${type}`,
      functionId: asString(telemetry.functionId),
      ...modelFields(options.model),
      maxRetries: options.maxRetries,
      // Normalize to the message-array shape the shared core (and v7's channel) expects: a bare string
      // `prompt` becomes a single user message, matching the SDK's own normalization.
      messages: normalizePromptMessages(options),
      ...recording(telemetry),
    },
  });
}

function normalizePromptMessages(options: Record<string, unknown>): unknown {
  if (Array.isArray(options.messages)) {
    return options.messages;
  }
  if (typeof options.prompt === 'string') {
    return [{ role: 'user', content: options.prompt }];
  }
  return options.messages ?? options.prompt;
}

function recording(telemetry: Record<string, unknown>): { recordInputs: unknown; recordOutputs: unknown } {
  return { recordInputs: telemetry.recordInputs, recordOutputs: telemetry.recordOutputs };
}

function modelFields(model: unknown): { provider?: string; modelId?: string } {
  return { provider: modelField(model, 'provider'), modelId: modelField(model, 'modelId') };
}

function modelField(model: unknown, field: 'modelId' | 'provider'): string | undefined {
  return isRecord(model) ? asString(model[field]) : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
